/**
 * Context Manager
 * Provides context-aware command enhancement and suggestions
 * Understands current app state to make commands smarter
 */

import { actionExecutor, AppAction } from './ActionExecutor';
import { getCompanyId } from '../tenantContext';

export interface AppContext {
  currentScreen: string;
  selectedWell?: any;
  selectedTicket?: any;
  selectedRoute?: any;
  recentlyViewed: Array<{ type: string; id: string; name?: string; timestamp: Date }>;
  userRole: string;
  userId: string;
  companyId: string;
  currentFilters?: any;
  pendingActions?: string[];
  wells?: any[];
  activeEntry?: any;
  clipboard?: any; // For "repeat last action" type commands
}

export interface ContextualSuggestion {
  text: string;
  command: string;
  action: string;
  priority: number; // 1-10, higher = more relevant
  reason: string;
  icon?: string;
}

export interface EnhancedCommand {
  original: string;
  enhanced: string;
  confidence: number;
  modifications: string[];
  context: Partial<AppContext>;
}

class ContextManager {
  private static instance: ContextManager;
  private currentContext: AppContext;
  private contextHistory: AppContext[] = [];
  private readonly MAX_HISTORY = 50;

  private constructor() {
    this.currentContext = this.createDefaultContext();
  }

  static getInstance(): ContextManager {
    if (!ContextManager.instance) {
      ContextManager.instance = new ContextManager();
    }
    return ContextManager.instance;
  }

  /**
   * Update the current context
   */
  updateContext(updates: Partial<AppContext>): void {
    // Save to history before updating
    this.contextHistory.push({ ...this.currentContext });
    if (this.contextHistory.length > this.MAX_HISTORY) {
      this.contextHistory.shift();
    }

    // Update context
    this.currentContext = {
      ...this.currentContext,
      ...updates
    };

    // Update recently viewed if applicable
    if (updates.selectedWell) {
      this.addToRecentlyViewed('well', updates.selectedWell.id, updates.selectedWell.name);
    }
    if (updates.selectedTicket) {
      this.addToRecentlyViewed('ticket', updates.selectedTicket.id, `Ticket ${updates.selectedTicket.ticket_number}`);
    }
    if (updates.selectedRoute) {
      this.addToRecentlyViewed('route', updates.selectedRoute.id, updates.selectedRoute.name);
    }
  }

  /**
   * Get current context
   */
  getContext(): AppContext {
    return { ...this.currentContext };
  }

  /**
   * Add to recently viewed items
   */
  private addToRecentlyViewed(type: string, id: string, name?: string): void {
    const item = {
      type,
      id,
      name,
      timestamp: new Date()
    };

    // Remove if already exists
    this.currentContext.recentlyViewed = this.currentContext.recentlyViewed.filter(
      i => !(i.type === type && i.id === id)
    );

    // Add to front
    this.currentContext.recentlyViewed.unshift(item);

    // Keep only last 20
    if (this.currentContext.recentlyViewed.length > 20) {
      this.currentContext.recentlyViewed.pop();
    }
  }

  /**
   * Enhance command with context
   */
  async enhanceCommand(command: string): Promise<EnhancedCommand> {
    const modifications: string[] = [];
    let enhanced = command;
    let confidence = 1.0;

    const lowerCommand = command.toLowerCase().trim();

    // 1. Add well context if needed
    if (this.needsWellContext(lowerCommand) && this.currentContext.selectedWell) {
      const wellName = this.currentContext.selectedWell.name;
      if (!lowerCommand.includes(wellName.toLowerCase())) {
        enhanced = `${enhanced} for well ${wellName}`;
        modifications.push(`Added well: ${wellName}`);
        confidence *= 0.95;
      }
    }

    // 2. Resolve "this" references
    if (lowerCommand.includes(' this ') || lowerCommand.startsWith('this ')) {
      enhanced = this.resolveThisReference(enhanced);
      if (enhanced !== command) {
        modifications.push('Resolved "this" reference');
        confidence *= 0.9;
      }
    }

    // 3. Resolve "it" or "that" references
    if (/\b(it|that)\b/i.test(lowerCommand)) {
      const resolved = this.resolvePronouns(enhanced);
      if (resolved !== enhanced) {
        enhanced = resolved;
        modifications.push('Resolved pronoun reference');
        confidence *= 0.9;
      }
    }

    // 4. Add time context for ambiguous commands
    if (this.needsTimeContext(lowerCommand)) {
      const now = new Date().getHours();
      if (now >= 6 && now < 12) {
        enhanced = `${enhanced} today morning`;
      } else if (now >= 12 && now < 18) {
        enhanced = `${enhanced} today afternoon`;
      }
      modifications.push('Added time context');
    }

    // 5. Use clipboard/last action for "repeat" or "again"
    if (/\b(repeat|again|same|last)\b/i.test(lowerCommand)) {
      if (this.currentContext.clipboard) {
        enhanced = this.currentContext.clipboard.command || enhanced;
        modifications.push('Using last action');
        confidence *= 0.85;
      }
    }

    // 6. Expand abbreviations
    const expanded = this.expandAbbreviations(enhanced);
    if (expanded !== enhanced) {
      enhanced = expanded;
      modifications.push('Expanded abbreviations');
    }

    return {
      original: command,
      enhanced,
      confidence,
      modifications,
      context: {
        currentScreen: this.currentContext.currentScreen,
        selectedWell: this.currentContext.selectedWell,
        selectedTicket: this.currentContext.selectedTicket
      }
    };
  }

  /**
   * Check if command needs well context
   */
  private needsWellContext(command: string): boolean {
    const needsWell = [
      'add entry',
      'create entry',
      'production',
      'check',
      'status',
      'schedule',
      'maintenance',
      'view history',
      'show data'
    ];

    return needsWell.some(phrase => command.includes(phrase)) && 
           !command.includes('well ') &&
           !command.includes('all wells');
  }

  /**
   * Check if command needs time context
   */
  private needsTimeContext(command: string): boolean {
    const needsTime = ['add entry', 'create entry', 'production data'];
    const hasTime = /\b(today|yesterday|tomorrow|last|this)\b/i.test(command);
    
    return needsTime.some(phrase => command.includes(phrase)) && !hasTime;
  }

  /**
   * Resolve "this" references
   */
  private resolveThisReference(command: string): string {
    let resolved = command;

    if (this.currentContext.selectedWell) {
      resolved = resolved.replace(
        /\bthis\s+well\b/gi,
        `well ${this.currentContext.selectedWell.name}`
      );
    }

    if (this.currentContext.selectedTicket) {
      resolved = resolved.replace(
        /\bthis\s+ticket\b/gi,
        `ticket ${this.currentContext.selectedTicket.ticket_number}`
      );
    }

    return resolved;
  }

  /**
   * Resolve pronouns (it, that)
   */
  private resolvePronouns(command: string): string {
    const recent = this.currentContext.recentlyViewed[0];
    if (!recent) return command;

    let resolved = command;
    const replacement = recent.name || `${recent.type} ${recent.id}`;

    resolved = resolved.replace(/\b(it|that)\b/gi, replacement);

    return resolved;
  }

  /**
   * Expand common abbreviations
   */
  private expandAbbreviations(command: string): string {
    const abbreviations: Record<string, string> = {
      'prod': 'production',
      'maint': 'maintenance',
      'svc': 'service',
      'tkt': 'ticket',
      'rte': 'route',
      'bbl': 'barrels',
      'mcf': 'thousand cubic feet',
      'psi': 'pounds per square inch',
      'tbg': 'tubing',
      'csg': 'casing'
    };

    let expanded = command;
    for (const [abbr, full] of Object.entries(abbreviations)) {
      const regex = new RegExp(`\\b${abbr}\\b`, 'gi');
      expanded = expanded.replace(regex, full);
    }

    return expanded;
  }

  /**
   * Get contextual suggestions
   */
  getContextualSuggestions(): ContextualSuggestion[] {
    const suggestions: ContextualSuggestion[] = [];
    const screen = this.currentContext.currentScreen;

    // Screen-specific suggestions
    if (screen === 'dashboard') {
      suggestions.push(
        {
          text: 'View my route',
          command: 'show my route',
          action: 'show_route',
          priority: 9,
          reason: 'Common dashboard action',
          icon: 'route'
        },
        {
          text: 'Show overdue wells',
          command: 'show overdue wells',
          action: 'filter_wells_overdue',
          priority: 8,
          reason: 'Important dashboard metric',
          icon: 'alert-triangle'
        },
        {
          text: 'Recent alerts',
          command: 'show recent alerts',
          action: 'show_alerts',
          priority: 7,
          reason: 'Stay updated',
          icon: 'bell'
        }
      );
    }

    if (screen === 'well-detail' && this.currentContext.selectedWell) {
      const wellName = this.currentContext.selectedWell.name;
      suggestions.push(
        {
          text: `Add production entry`,
          command: `add entry for well ${wellName}`,
          action: 'create_well_entry',
          priority: 10,
          reason: 'Primary well action',
          icon: 'plus-circle'
        },
        {
          text: 'View production history',
          command: `show production history for well ${wellName}`,
          action: 'show_production_history',
          priority: 8,
          reason: 'Common well view',
          icon: 'bar-chart'
        },
        {
          text: 'Schedule maintenance',
          command: `schedule maintenance for well ${wellName}`,
          action: 'schedule_service',
          priority: 7,
          reason: 'Maintenance planning',
          icon: 'wrench'
        },
        {
          text: 'Create service ticket',
          command: `create ticket for well ${wellName}`,
          action: 'create_ticket',
          priority: 6,
          reason: 'Report issues',
          icon: 'clipboard'
        }
      );
    }

    if (screen === 'wells-list') {
      suggestions.push(
        {
          text: 'Create optimized route',
          command: 'create route from all wells',
          action: 'create_route',
          priority: 9,
          reason: 'Plan your day',
          icon: 'map'
        },
        {
          text: 'Filter by status',
          command: 'show active wells',
          action: 'filter_wells',
          priority: 7,
          reason: 'Find specific wells',
          icon: 'filter'
        }
      );
    }

    if (screen === 'service-tickets') {
      suggestions.push(
        {
          text: 'Create new ticket',
          command: 'create service ticket',
          action: 'create_ticket',
          priority: 9,
          reason: 'Report an issue',
          icon: 'plus-circle'
        },
        {
          text: 'Show my tickets',
          command: 'show my assigned tickets',
          action: 'filter_tickets',
          priority: 8,
          reason: 'Track your work',
          icon: 'user'
        }
      );
    }

    // Time-based suggestions
    const hour = new Date().getHours();
    if (hour >= 6 && hour < 9 && screen === 'dashboard') {
      suggestions.unshift({
        text: '☀️ Start daily route',
        command: 'show my route and navigate to first well',
        action: 'start_route',
        priority: 10,
        reason: 'Morning routine',
        icon: 'sunrise'
      });
    }

    // Pending actions
    if (this.currentContext.pendingActions && this.currentContext.pendingActions.length > 0) {
      suggestions.unshift({
        text: `⚠️ ${this.currentContext.pendingActions.length} pending actions`,
        command: 'show pending actions',
        action: 'show_pending',
        priority: 10,
        reason: 'Complete unfinished work',
        icon: 'alert-circle'
      });
    }

    // Recently viewed quick access
    if (this.currentContext.recentlyViewed.length > 0) {
      const recent = this.currentContext.recentlyViewed[0];
      if (recent.type === 'well' && screen !== 'well-detail') {
        suggestions.push({
          text: `Return to ${recent.name}`,
          command: `show ${recent.name}`,
          action: 'navigate_to_well',
          priority: 6,
          reason: 'Recently viewed',
          icon: 'arrow-left'
        });
      }
    }

    // Sort by priority
    return suggestions.sort((a, b) => b.priority - a.priority);
  }

  /**
   * Get smart completions as user types
   */
  getSmartCompletions(partialCommand: string): string[] {
    const completions: string[] = [];
    const lower = partialCommand.toLowerCase();

    // Get all registered actions
    const actions = actionExecutor.getAllActions();

    // Match against examples
    for (const action of actions) {
      for (const example of action.examples) {
        if (example.toLowerCase().startsWith(lower) && example !== partialCommand) {
          completions.push(example);
        }
      }
    }

    // Add context-aware completions
    if (this.currentContext.selectedWell) {
      const wellName = this.currentContext.selectedWell.name;
      if (lower.includes('well') && !lower.includes(wellName.toLowerCase())) {
        completions.push(`${partialCommand} ${wellName}`);
      }
    }

    // Limit to top 5 most relevant
    return completions.slice(0, 5);
  }

  /**
   * Predict next likely command based on patterns
   */
  async predictNextCommand(): Promise<string | null> {
    // Analyze recent context history
    if (this.contextHistory.length < 3) return null;

    const recent = this.contextHistory.slice(-3);
    
    // Pattern: If user just viewed a well detail, likely to add entry
    const lastScreen = recent[recent.length - 1].currentScreen;
    const selectedWell = recent[recent.length - 1].selectedWell;

    if (lastScreen === 'well-detail' && selectedWell) {
      return `add entry for well ${selectedWell.name}`;
    }

    // Pattern: If user viewed wells list, likely to create route
    if (lastScreen === 'wells-list') {
      return 'create route';
    }

    return null;
  }

  /**
   * Reset context (e.g., when user logs out)
   */
  reset(): void {
    this.currentContext = this.createDefaultContext();
    this.contextHistory = [];
  }

  /**
   * Create default context
   */
  private createDefaultContext(): AppContext {
    return {
      currentScreen: 'dashboard',
      recentlyViewed: [],
      userRole: '',
      userId: '',
      companyId: getCompanyId(),
      pendingActions: [],
      wells: []
    };
  }

  /**
   * Export context for debugging
   */
  exportContext(): {
    current: AppContext;
    history: AppContext[];
  } {
    return {
      current: this.currentContext,
      history: this.contextHistory
    };
  }
}

// Export singleton
export const contextManager = ContextManager.getInstance();
